














































import { defineComponent, ref, onMounted, reactive, PropType, onBeforeUnmount } from '@vue/composition-api';
import OlbButton from '../button/button.vue';
import OlbIcon from '../icon/icon.vue';
import Flickity from 'flickity';
import { VNode } from 'vue/types/umd';

export type CarouselOptions = Flickity.Options & {
	controlsContainer?: string;
	prevNextButtons?: boolean;
};

export default defineComponent({
	components: {
		OlbButton,
		OlbIcon,
	},
	props: {
		options: {
			type: Object as PropType<CarouselOptions>,
		},
	},
	setup({ options }, { slots, emit }) {
		const state = reactive({
			cells: [] as Element[],
			selectedIndex: 0 as number | undefined,
		});
		const carouselRef = ref<HTMLElement | null>(null);
		const pageDotsRef = ref<HTMLElement | null>(null);
		const buttonPrevRef = ref<HTMLElement | null>(null);
		const buttonNextRef = ref<HTMLElement | null>(null);
		const defaultOptions = {
			prevNextButtons: true,
			imagesLoaded: true,
		};
		/**
		 * Merging default and props options
		 */
		const derivedOptions: CarouselOptions = {
			...defaultOptions,
			...options,
		};
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		let flickity: any;
		/**
		 * Previous slide
		 * @returns {void}
		 */
		function prev(): void {
			flickity.previous();
		}
		/**
		 * Next slide
		 * @returns {void}
		 */
		function next(): void {
			flickity.next();
		}
		/**
		 * Check the amount of slots to determine whether to carousel or not
		 */
		function isCarousel(): boolean {
			if (slots.default === undefined) {
				return false;
			}
			const slides = slots.default().filter((vnode: VNode) => vnode.tag !== undefined);
			return slides.length > 1;
		}
		/**
		 * Apply buttons only if it is a carousel and by options
		 */
		function showButtons(): boolean | undefined {
			return isCarousel() && derivedOptions.prevNextButtons;
		}
		/**
		 * Apply buttons only if it is a carousel and by options
		 */
		function showPageDots(): boolean | undefined {
			return isCarousel() && slots['pagedots'] !== undefined;
		}
		/**
		 * Check for first / last cell to disable buttons if wrapAround is disabled
		 */
		function showButton(dir: 'prev' | 'next'): boolean {
			if (!derivedOptions.wrapAround) {
				if (dir === 'prev') {
					return state.selectedIndex !== 0;
				}
				if (dir === 'next') {
					return (
						-flickity?.slidesWidth !== Math.round(flickity?.x) &&
						state.cells.length - 1 !== state.selectedIndex
					);
				}
			}
			return true;
		}
		/**
		 * Select slide based on cell index
		 */
		function selectSlide(cell: number) {
			flickity.select(cell);
		}
		/**
		 * Get the slide index to use in other components.
		 */
		function onSlideChange() {
			state.selectedIndex = flickity.selectedIndex;
			emit('indexChanged', state.selectedIndex);
		}
		/**
		 * Method to set position of controls by selector given from controlsContainer option
		 */
		function setBoundingPosition() {
			const element =
				derivedOptions.controlsContainer &&
				flickity.selectedElement.querySelector(derivedOptions.controlsContainer);
			if (pageDotsRef.value) {
				pageDotsRef.value.style.top = `${(element && element.clientHeight) || 0}px`;
			}
			if (buttonPrevRef.value && buttonNextRef.value && element) {
				buttonPrevRef.value.style.cssText = `
                    height: ${element.clientHeight}px !important;
                `;
				buttonNextRef.value.style.cssText = `
                    height: ${element.clientHeight}px !important;
                `;
			}
		}
		/**
		 * Create flickity carousel
		 */
		function createCarousel() {
			/**
			 * Calling flickity library (relies on window) only if window is present
			 * for SSR support
			 */
			if (typeof window !== undefined && typeof document !== undefined) {
				// eslint-disable-next-line @typescript-eslint/no-var-requires
				const Flickity = require('flickity');
				// eslint-disable-next-line @typescript-eslint/no-var-requires
				const imagesLoaded = require('imagesloaded');
				require('flickity-imagesloaded');
				/**
				 * Adding imagesloaded dispatchEvent to flickity for position calculation based on detached image loading
				 */
				Flickity.prototype.imagesLoaded = function() {
					if (!this.options.imagesLoaded) {
						return;
					}
					const onImagesLoadedProgress = (instance: {}, image: { img: string }) => {
						const cell = this.getParentCell(image.img);
						this.cellSizeChange(cell && cell.element);
						if (!this.options.freeScroll) {
							this.positionSliderAtSelected();
						}
						this.dispatchEvent('imagesLoadedProgress', null, [image.img, cell.element]);
					};
					imagesLoaded(this.slider).on('progress', onImagesLoadedProgress);
				};
				/**
				 * Creating a carousel instance by ref (element) with derived options
				 * disabling prev-next buttons by default to enable custom buttons
				 */
				flickity = new Flickity(carouselRef.value, {
					...derivedOptions,
					prevNextButtons: false,
					pageDots: false,
				});

				/**
				 * In some cases overflow-y generates a negative transform.
				 * To get rid of it resize() regenerates the size.
				 * https://github.com/metafizzy/flickity/issues/902
				 */
				flickity.resize();
				state.cells = flickity.cells;
				flickity.on('change', onSlideChange);
				if (derivedOptions.controlsContainer) {
					flickity.on('imagesLoadedProgress', () => {
						setBoundingPosition();
					});
					/**
					 * Debouncing resize to 150ms to keep up on Flickity's resize debounce
					 */
					let resizeTimer: ReturnType<typeof setTimeout>;
					window.addEventListener('resize', () => {
						clearTimeout(resizeTimer);
						resizeTimer = setTimeout(function() {
							setBoundingPosition();
						}, 150);
					});
				}
			}
			emit('createCarousel', () => flickity.resize());
		}

		function onKeyDown(e: KeyboardEvent) {
			if (e.key === 'ArrowLeft') {
				flickity.previous();
			}
			if (e.key === 'ArrowRight') {
				flickity.next();
			}
		}

		onMounted(() => {
			if (isCarousel()) {
				createCarousel();
				window.addEventListener('keydown', onKeyDown);
			}
		});

		onBeforeUnmount(() => {
			window.removeEventListener('keydown', onKeyDown);
		});

		return {
			carouselRef,
			pageDotsRef,
			buttonPrevRef,
			buttonNextRef,
			derivedOptions,
			createCarousel,
			prev,
			next,
			showPageDots,
			showButtons,
			showButton,
			selectSlide,
			state,
		};
	},
});
